import {Component, computed, inject, OnInit, signal} from '@angular/core';
import {CommonModule} from '@angular/common';
import {FormBuilder, ReactiveFormsModule, Validators} from '@angular/forms';
import {InputTextModule} from 'primeng/inputtext';
import {ButtonModule} from 'primeng/button';
import {BaseStepComponent} from '../../../../../shared/components/wizard/base-step.component';
import {CompanyWizardService} from '../company-wizard.service';
import {debounceTime, distinctUntilChanged} from 'rxjs/operators';
import {CreateUpdateCompanyRequest} from '@frontend/shared-api';
import {Subject} from 'rxjs';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {Panel} from 'primeng/panel';
import {CountryService} from '@frontend/shared';
import {Message} from 'primeng/message';
import {TranslocoDirective} from '@jsverse/transloco';
import {Tooltip} from 'primeng/tooltip';

interface Office {
  name: string;
  address: string;
  isDeletable?: boolean;
}

interface City {
  uuid: string;
  name: string;
  countryCode: string;
  offices: Office[];
}

interface OfficeUpdate {
  cityId: string;
  index: number;
  value: string;
  field: 'name' | 'address';
}

@Component({
  selector: 'app-company-wizard-office-step',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    InputTextModule,
    ButtonModule,
    Panel,
    Message,
    TranslocoDirective,
    Tooltip,
  ],
  templateUrl: './company-wizard-office-step.component.html',
  styleUrls: ['./company-wizard-office-step.component.scss'],
})
export class CompanyWizardOfficeStepComponent
  extends BaseStepComponent<CreateUpdateCompanyRequest>
  implements OnInit
{
  override readonly stepIndex = 4;

  private readonly fb = inject(FormBuilder);
  private readonly companyWizardService = inject(CompanyWizardService);
  protected readonly countryService = inject(CountryService);
  private readonly officeUpdateSubject = new Subject<OfficeUpdate>();
  private readonly isEditing = signal(false);
  protected readonly touchedFields = signal<{
    [cityId: string]: Array<{name: boolean; address: boolean}>;
  }>({});

  readonly form = this.fb.group({
    offices: this.fb.control<{[cityId: string]: Office[]}>({}, [Validators.required]),
  });

  cities = signal<City[]>([]);
  offices = signal<{[cityId: string]: Office[]}>({});

  private readonly hasOffices = computed(() => Object.keys(this.offices()).length > 0);

  private readonly allOfficesAreValid = computed(() => {
    return Object.values(this.offices()).every((cityOffices) =>
      cityOffices.every(
        (office) => office.name.trim().length > 0 && office.address.trim().length > 0,
      ),
    );
  });

  readonly isValidComputed = computed(
    () => this.hasOffices() && this.allOfficesAreValid() && !this.isEditing(),
  );

  constructor() {
    super();
    this.setupOfficeUpdates();
  }

  override ngOnInit(): void {
    super.ngOnInit();
    this.initializeOfficesData();
  }

  addOffice(cityId: string): void {
    if (!cityId) {
      console.error('Cannot add office: city id is required');
      return;
    }
    try {
      this.addOfficeToCity(cityId);
      this.syncCityWithOffices(cityId);
      this.updateForm();
    } catch (error) {
      console.error(`Failed to add office to ${cityId}:`, error);
    }
  }

  removeOffice(cityId: string, index: number): void {
    if (!cityId || index < 0) {
      console.error('Cannot remove office: invalid city id or index');
      return;
    }
    try {
      const officesMap = this.offices();
      const cityOffices = officesMap[cityId];

      if (!cityOffices || cityOffices.length <= 1) {
        console.warn(`Cannot remove the only office for ${cityId}`);
        return;
      }
      this.removeOfficeFromCity(cityId, index);
      this.removeTouchedFieldForOffice(cityId, index);
      this.syncCityWithOffices(cityId);
      this.updateForm();
    } catch (error) {
      console.error(`Failed to remove office from ${cityId} at index ${index}:`, error);
    }
  }

  onOfficeNameChange(cityId: string, index: number, value: string): void {
    if (!this.isEditing()) {
      this.isEditing.set(true);
    }
    this.officeUpdateSubject.next({cityId, index, value, field: 'name'});
  }

  onOfficeAddressChange(cityId: string, index: number, value: string): void {
    if (!this.isEditing()) {
      this.isEditing.set(true);
    }
    this.officeUpdateSubject.next({cityId, index, value, field: 'address'});
  }

  shouldShowOfficeNameError(cityId: string, index: number): boolean {
    const isTouched = this.touchedFields()[cityId]?.[index]?.name || false;
    const office = this.offices()[cityId]?.[index];
    const isEmpty = !office?.name || office.name.trim() === '';
    return isTouched && isEmpty && !this.isEditing();
  }

  shouldShowOfficeAddressError(cityId: string, index: number): boolean {
    const isTouched = this.touchedFields()[cityId]?.[index]?.address || false;
    const office = this.offices()[cityId]?.[index];
    const isEmpty = !office?.address || office.address.trim() === '';
    return isTouched && isEmpty && !this.isEditing();
  }

  onOfficeNameBlur(cityId: string, index: number, currentValue: string): void {
    this.handleOfficeUpdate({cityId, index, value: currentValue, field: 'name'});
    const cityTouched = this.touchedFields()[cityId] || [];
    const updatedTouched = [...cityTouched];
    if (!updatedTouched[index]) {
      updatedTouched[index] = {name: false, address: false};
    }
    updatedTouched[index].name = true;
    this.touchedFields.update((prev) => ({
      ...prev,
      [cityId]: updatedTouched,
    }));
  }

  onOfficeAddressBlur(cityId: string, index: number, currentValue: string): void {
    this.handleOfficeUpdate({cityId, index, value: currentValue, field: 'address'});
    const cityTouched = this.touchedFields()[cityId] || [];
    const updatedTouched = [...cityTouched];
    if (!updatedTouched[index]) {
      updatedTouched[index] = {name: false, address: false};
    }
    updatedTouched[index].address = true;
    this.touchedFields.update((prev) => ({
      ...prev,
      [cityId]: updatedTouched,
    }));
  }

  private initializeOfficesData(): void {
    if (!this.formData?.countries || !this.isValidCompanyRequest(this.formData)) {
      return;
    }

    const citiesArray: City[] = this.cities().length
      ? this.cities()
      : this.formData.countries.reduce((acc: City[], country) => {
          (country.cities || []).forEach((city) => {
            acc.push({
              uuid: (city as City).uuid,
              name: city.name,
              countryCode: country.countryCode,
              offices: city.offices || [],
            });
          });
          return acc;
        }, []);

    this.cities.set(citiesArray);

    const officesObj: {[cityId: string]: Office[]} = {};
    citiesArray.forEach((city) => {
      const savedOffices = city.offices || [];
      officesObj[city.uuid] =
        savedOffices.length > 0
          ? savedOffices.map((office) => ({
              name: office.name,
              address: office.address,
              isDeletable: office.isDeletable,
            }))
          : [{name: '', address: ''}];
    });

    this.offices.set(officesObj);

    this.updateForm();
  }

  private setupOfficeUpdates(): void {
    this.officeUpdateSubject
      .pipe(
        debounceTime(500),
        distinctUntilChanged(
          (prev, curr) =>
            prev.cityId === curr.cityId &&
            prev.index === curr.index &&
            prev.value === curr.value &&
            prev.field === curr.field,
        ),
        takeUntilDestroyed(),
      )
      .subscribe((update) => {
        this.handleOfficeUpdate(update);
        this.isEditing.set(false);
      });
  }

  private handleOfficeUpdate(update: OfficeUpdate): void {
    const currentOffices = {...this.offices()};
    if (!currentOffices[update.cityId]) {
      currentOffices[update.cityId] = [{name: '', address: ''}];
    }
    currentOffices[update.cityId][update.index][update.field] = update.value;
    this.offices.set(currentOffices);
    this.updateForm();
  }

  private isValidCompanyRequest(
    data: Partial<CreateUpdateCompanyRequest>,
  ): data is CreateUpdateCompanyRequest {
    return (
      !!data.company &&
      Array.isArray(data.countries) &&
      data.countries.every((country) => !!country.countryCode && Array.isArray(country.cities))
    );
  }

  private updateForm(): void {
    this.form.patchValue({offices: this.offices()}, {emitEvent: false});

    this.companyWizardService.updateStepData({
      stepIndex: this.stepIndex,
      data: {offices: this.offices()},
    });
  }

  private addOfficeToCity(cityId: string): void {
    const officesMap = {...this.offices()};
    if (!officesMap[cityId] || !Array.isArray(officesMap[cityId])) {
      officesMap[cityId] = [];
    }
    officesMap[cityId].push({name: '', address: ''});
    this.offices.set(officesMap);
  }

  private removeOfficeFromCity(cityId: string, index: number): void {
    const officesMap = {...this.offices()};
    officesMap[cityId] = officesMap[cityId].filter((_, i) => i !== index);
    this.offices.set(officesMap);
  }

  private syncCityWithOffices(cityId: string): void {
    const citiesList = [...this.cities()];
    const officesMap = this.offices();
    const cityIndex = citiesList.findIndex((city) => city.uuid === cityId);
    if (cityIndex !== -1 && officesMap[cityId]) {
      citiesList[cityIndex] = {
        ...citiesList[cityIndex],
        offices: [...officesMap[cityId]],
      };
      this.cities.set(citiesList);
    }
  }

  private removeTouchedFieldForOffice(cityId: string, removedIndex: number): void {
    this.touchedFields.update((prev) => {
      const cityTouched: Array<{name: boolean; address: boolean}> = prev[cityId]
        ? [...prev[cityId]]
        : [];
      cityTouched.splice(removedIndex, 1);
      return {...prev, [cityId]: cityTouched};
    });
  }

  isOfficeDeletable(cityId: string, index: number): boolean {
    if (!this.companyWizardService.isEdit()) {
      return true;
    }

    const cityOffices = this.offices()[cityId];
    if (!cityOffices?.[index]) {
      return true;
    }

    const stepData = this.companyWizardService.getStepData<any>(this.stepIndex);
    if (!stepData?.offices?.[cityId]) {
      return true;
    }

    const officeInStepData = stepData.offices[cityId][index];

    return officeInStepData?.isDeletable !== false;
  }
}
